import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  CustomInput,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { CurrencyContext } from "../../context/Context";
import { fetchInitialDebtVoidPreview, voidInitialDebt } from "../../helpers/api/condominium";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { Toast } from "../UI/toast/Toast";

const MIN_REASON_LENGTH = 5;

const VoidInitialDebtModal = ({ isOpen, toggle, condominiumId, propertyId, periodId, onVoided }) => {
  const { baseCurrency } = useContext(CurrencyContext);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isWhole = !propertyId;
  const title = isWhole ? "Anular toda la deuda inicial" : "Anular deuda inicial";

  useEffect(() => {
    if (!isOpen || !condominiumId) {
      return undefined;
    }

    let isMounted = true;
    setPreview(null);
    setSelected([]);
    setBlockers([]);
    setReason("");
    setIsLoading(true);

    fetchInitialDebtVoidPreview(condominiumId, propertyId, periodId)
      .then((response) => {
        if (!isMounted) return;
        const data = response?.data || null;
        setPreview(data);
        setSelected((Array.isArray(data?.debts) ? data.debts : []).filter((debt) => debt.can_void).map((debt) => debt.id));
      })
      .catch((error) => {
        if (isMounted) handleErrorMessage(error);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, condominiumId, propertyId, periodId]);

  const money = (value) =>
    `${baseCurrency?.symbol || "$"} ${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const debts = !isWhole && Array.isArray(preview?.debts) ? preview.debts : [];
  const blocked = isWhole ? preview?.blocked || [] : [];
  const selectedTotal = debts.filter((debt) => selected.includes(debt.id)).reduce((sum, debt) => sum + Number(debt.amount || 0), 0);
  const trimmedReason = reason.trim();
  const hasSomething = isWhole ? Boolean(preview?.can_void) : selected.length > 0;
  const hasVoidable = isWhole ? Boolean(preview?.can_void) : debts.some((debt) => debt.can_void);
  const canSubmit = hasSomething && blockers.length === 0 && trimmedReason.length >= MIN_REASON_LENGTH && !isSubmitting;

  const toggleDebt = (id) =>
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const close = () => {
    if (!isSubmitting) toggle();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      const response = await voidInitialDebt(
        condominiumId,
        propertyId,
        isWhole ? { reason: trimmedReason } : { reason: trimmedReason, debt_ids: selected }
      );
      const count = response?.data?.debts || 0;
      Toast.fire({
        type: "success",
        title: count === 1 ? "Deuda inicial anulada." : `Se anularon ${count} deudas iniciales.`,
      });
      toggle();
      if (onVoided) onVoided();
    } catch (error) {
      const serverBlockers = error?.data?.blockers;
      if (Array.isArray(serverBlockers) && serverBlockers.length > 0) {
        setBlockers(serverBlockers);
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderUnit = () => {
    if (!debts.length) {
      return (
        <Alert color="info" className="mb-0">
          {periodId ? "Esta unidad no tiene deuda inicial en este período." : "Esta unidad no tiene deuda inicial cargada."}
        </Alert>
      );
    }

    return (
      <>
        <p className="mb-2">
          {`Unidad: `}
          <strong>{preview?.property?.name}</strong>
        </p>
        <FormGroup>
          {debts.map((debt) => (
            <div key={debt.id} className="mb-2">
              <CustomInput
                type="checkbox"
                id={`initial-debt-${debt.id}`}
                checked={selected.includes(debt.id)}
                disabled={!debt.can_void || isSubmitting}
                onChange={() => toggleDebt(debt.id)}
                label={`${debt.period || "Sin período"} · ${debt.concept} · ${money(debt.amount)}`}
              />
              {debt.blockers.length > 0 && (
                <ul className="text-danger pl-4 mb-0">
                  {debt.blockers.map((blocker, index) => (
                    <li key={index}>{blocker}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </FormGroup>
        {selected.length > 0 && <p className="mb-3">{`Se anulan ${selected.length} deuda(s) por ${money(selectedTotal)}.`}</p>}
      </>
    );
  };

  const renderWhole = () => (
    <>
      {preview?.can_void ? (
        <p className="mb-2">
          {`Se anulan ${preview.debts} deuda(s) inicial(es) de ${preview.units} unidad(es) por ${money(preview.total)}. `}
          No toca el saldo a favor: si también está mal, corrígelo en el archivo y vuelve a subirlo.
        </p>
      ) : (
        <Alert color="info" className="mb-2">
          No hay deuda inicial que se pueda anular en este condominio.
        </Alert>
      )}
      {blocked.length > 0 && (
        <Alert color="warning" className="mb-3">
          <p className="font-weight-semi-bold mb-1">{`Estas ${blocked.length} no se anulan:`}</p>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            <ul className="pl-3 mb-0">
              {blocked.map((debt) => (
                <li key={debt.id}>{`${debt.unit} · ${debt.period || "Sin período"} · ${money(debt.amount)}: ${debt.blockers.join(" ")}`}</li>
              ))}
            </ul>
          </div>
        </Alert>
      )}
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={close} centered size={isWhole ? "lg" : undefined}>
      <ModalHeader toggle={close}>{title}</ModalHeader>
      <ModalBody className="fs--1">
        {isLoading ? (
          <div className="text-center py-3">
            <Spinner color="primary" />
          </div>
        ) : (
          preview && (
            <>
              {blockers.length > 0 && (
                <Alert color="danger">
                  <p className="mb-1 font-weight-semi-bold">No se puede anular:</p>
                  <ul className="mb-0 pl-3">
                    {blockers.map((blocker, index) => (
                      <li key={index}>{blocker}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              {isWhole ? renderWhole() : renderUnit()}
              {hasSomething && (
                <>
                  {preview.effects?.length > 0 && (
                    <>
                      <p className="mb-1 font-weight-semi-bold">Al anular:</p>
                      <ul className="pl-3">
                        {preview.effects.map((effect, index) => (
                          <li key={index}>{effect}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  <FormGroup className="mb-0">
                    <Label for="initial-debt-void-reason">Motivo de la anulación *</Label>
                    <Input
                      id="initial-debt-void-reason"
                      type="textarea"
                      rows={3}
                      maxLength={1000}
                      value={reason}
                      disabled={isSubmitting || blockers.length > 0}
                      placeholder="Explica por qué se anula. Queda registrado en la deuda."
                      onChange={(event) => setReason(event.target.value)}
                    />
                    <small className="text-600">La deuda no se borra: queda como Anulada con este motivo.</small>
                  </FormGroup>
                </>
              )}
            </>
          )
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" onClick={close} disabled={isSubmitting}>
          Volver
        </Button>
        {hasVoidable && (
          <Button color="danger" onClick={submit} disabled={!canSubmit}>
            {isSubmitting ? <Spinner size="sm" /> : title}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

VoidInitialDebtModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  periodId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onVoided: PropTypes.func,
};

export default VoidInitialDebtModal;
